import { Button } from '@/components/ui/button'
import { ReactNode, useEffect, useState, useRef } from 'react'

/**
 * The footer row belonging to this table — the one holding the count, marked
 * with `data-table-footer`. Walks up from the table container so a page with
 * more than one table matches the nearest, and gives up rather than grabbing
 * an unrelated table's footer from elsewhere in the document.
 */
function findTableFooter(container: HTMLElement): HTMLElement | null {
  let scope: HTMLElement | null = container.parentElement
  for (let depth = 0; scope && depth < 5; depth++) {
    const footer = scope.querySelector<HTMLElement>('[data-table-footer]')
    if (footer) return footer
    scope = scope.parentElement
  }
  return null
}

interface SelectionActionBarProps {
  selectedCount: number
  isVisible: boolean
  onClearSelection: () => void
  children: ReactNode
  className?: string
  itemName?: {
    singular: string
    plural: string
  }
  tableContainerRef?: React.RefObject<HTMLElement | null>
  selectedRowIds?: string[] // Kept for backwards compatibility but not used
}

export function SelectionActionBar({
  selectedCount,
  isVisible,
  onClearSelection,
  children,
  className = '',
  itemName = { singular: 'person', plural: 'people' },
  tableContainerRef,
  selectedRowIds: _selectedRowIds = []
}: SelectionActionBarProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isVisible || !tableContainerRef?.current) {
      setPosition(null)
      return
    }

    const updatePosition = () => {
      const container = tableContainerRef.current
      if (!container) return

      // Get bounding rect of the container (the visible scrollable area)
      const containerRect = container.getBoundingClientRect()
      const viewportHeight = window.innerHeight

      // Center horizontally relative to the container
      const left = containerRect.left + (containerRect.width / 2)

      // Use the container's visible bottom as the reference point
      const containerBottom = containerRect.bottom
      // Measure rather than assume: the bar's height depends on its children.
      // offsetHeight, not getBoundingClientRect, so a transform can never skew
      // the measurement.
      const actionBarHeight = barRef.current?.offsetHeight || 56
      // Same gutter the table footers use, read from the design token so the
      // bar keeps step if the token changes.
      const rootStyles = getComputedStyle(document.documentElement)
      const remPx = parseFloat(rootStyles.fontSize) || 16
      const rawGutter = rootStyles.getPropertyValue('--spacing-gutter').trim()
      const gutter = rawGutter.endsWith('rem')
        ? parseFloat(rawGutter) * remPx
        : parseFloat(rawGutter) || 12

      let top: number

      const spaceBelow = viewportHeight - containerBottom

      // The footer row (the one holding the count) already encodes the
      // spacing for this table, whether it runs to the bottom of the screen
      // or sits inside a padded card. Centre on it when we can find it, so
      // the bar and the count always share a line.
      const footer = findTableFooter(container)
      if (footer) {
        const footerRect = footer.getBoundingClientRect()
        top = footerRect.top + (footerRect.height - actionBarHeight) / 2
      } else if (spaceBelow >= actionBarHeight + gutter * 2) {
        // No footer row to align to: sit one gutter below the table. The
        // clamp matters when the table is short and the space below is most
        // of the page — centring there would strand the bar in the middle of
        // the empty area instead of near its table.
        top = containerBottom + Math.min(gutter, (spaceBelow - actionBarHeight) / 2)
      } else {
        // Not enough room below the table (a page that scrolls): sit the bar
        // one gutter above the bottom of the viewport.
        top = viewportHeight - actionBarHeight - gutter
      }

      setPosition({ top, left })
    }

    updatePosition()
    
    // Update on scroll and resize
    const handleUpdate = () => updatePosition()
    window.addEventListener('scroll', handleUpdate, true)
    window.addEventListener('resize', handleUpdate)

    return () => {
      window.removeEventListener('scroll', handleUpdate, true)
      window.removeEventListener('resize', handleUpdate)
    }
  }, [isVisible, tableContainerRef])

  if (!isVisible) return null

  // Fallback if no tableContainerRef - center at bottom of screen
  const useFallback = !tableContainerRef || !position

  return (
    <>
      <div
        ref={barRef}
        // Every button on this bar is the same height, whatever a caller
        // passes: mixed sizes here read as a mistake rather than a hierarchy.
        // h-10 matches the Button `xs` size the bar's own Unselect uses.
        className={`fixed flex items-center bg-surface-raised/95 border p-2 pl-8 min-h-table-footer z-50 rounded-full backdrop-blur-sm border-border shadow-lg [&_button]:h-10 ${
          isVisible
            ? 'opacity-100' 
            : 'opacity-0 pointer-events-none'
        } ${className}`}
        style={{
          top: useFallback ? 'auto' : `${position.top}px`,
          bottom: useFallback ? '20px' : 'auto',
          left: useFallback ? '50%' : `${position.left}px`,
          transform: 'translate(-50%, 0)',
          minWidth: '360px',
          // Fade only. The entrance used to be a keyframe animation that
          // offset and scaled the bar; on pages that re-render often it was
          // restarted every render and never advanced past its first frame,
          // leaving the bar permanently 10px high and 5% small. Nothing that
          // moves the bar belongs in an animation that can be interrupted.
          transition: 'opacity 0.15s ease-out',
          boxShadow: '0 8px 20px 10px rgb(var(--shadow-tint-wide) / 0.25)',
        }}
      >
      <div className="mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs whitespace-nowrap mr-1">
            {selectedCount} {selectedCount === 1 ? itemName.singular : itemName.plural} selected
          </span>
          <Button variant="secondary" size="xs" onClick={onClearSelection}>
            Unselect
          </Button>
        </div>
        <div className="flex items-center gap-2 ml-3">
          {children}
        </div>
      </div>
    </div>
    </>
  )
}

